import React from 'react';
import { 
  Box,
  Typography,
  Button,
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper,
  Stack,
  CircularProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';

const SectionContainer = styled(Box)(({ theme }) => ({
  marginBottom: '16px',
  position: 'relative',
  [theme.breakpoints.up('sm')]: {
    marginBottom: '24px'
  },
  [theme.breakpoints.up('md')]: {
    marginBottom: '32px'
  }
}));

const NoDataContainer = styled(Box)(({ theme }) => ({
  padding: '24px 16px',
  textAlign: 'center',
  backgroundColor: '#f9fafb',
  borderRadius: '4px',
  marginBottom: '12px',
  [theme.breakpoints.up('sm')]: {
    padding: '32px 20px',
    marginBottom: '16px'
  },
  [theme.breakpoints.up('md')]: {
    padding: '40px 20px'
  }
}));

const NoDataText = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '13px',
  fontWeight: 500,
  color: '#6b7280',
  [theme.breakpoints.up('sm')]: {
    fontSize: '14px'
  }
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 600,
  color: '#121927',
  marginBottom: '12px',
  [theme.breakpoints.up('sm')]: {
    fontSize: '15px',
    marginBottom: '14px'
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '16px',
    marginBottom: '16px'
  }
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: '4px',
  border: 'none',
  boxShadow: 'none',
  backgroundColor: 'transparent',
  marginBottom: '12px',
  overflowX: 'auto',
  WebkitOverflowScrolling: 'touch',
  '& table': {
    minWidth: '550px'
  },
  [theme.breakpoints.up('sm')]: {
    marginBottom: '16px'
  },
  [theme.breakpoints.up('md')]: {
    '& table': {
      minWidth: '650px'
    }
  }
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  backgroundColor: '#0B91D6'
}));

const HeaderCell = styled(TableCell)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '12px',
  fontWeight: 500,
  color: '#FFFFFF',
  padding: '12px',
  borderBottom: 'none',
  [theme.breakpoints.up('sm')]: {
    fontSize: '13px',
    padding: '14px'
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '14px',
    padding: '16px'
  }
}));

const DataCell = styled(TableCell)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '11px',
  fontWeight: 500,
  color: '#121927',
  padding: '12px',
  borderBottom: '1px solid #F0F0F0',
  [theme.breakpoints.up('sm')]: {
    fontSize: '12px',
    padding: '14px'
  },
  [theme.breakpoints.up('md')]: {
    padding: '16px'
  }
}));

const VolumeContainer = styled(Stack)(({ theme }) => ({
  flexDirection: 'column',
  gap: '8px'
}));

const VolumeText = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '12px',
  fontWeight: 500,
  color: '#121927'
}));



const LocalRelevanceBadge = styled(Box)(({ relevance }) => ({
  display: 'inline-block',
  padding: '4px 8px',
  borderRadius: '4px',
  fontSize: '10px',
  fontWeight: 600,
  fontFamily: 'Inter, sans-serif',
  backgroundColor: relevance?.includes('High') ? '#dcfce7' : 
                   relevance?.includes('Medium') ? '#fef3c7' : '#f3f4f6',
  color: relevance?.includes('High') ? '#166534' : 
         relevance?.includes('Medium') ? '#92400e' : '#4b5563'
}));

const CompetitionBadge = styled(Box)(({ level }) => ({
  display: 'inline-block',
  padding: '4px 8px',
  borderRadius: '4px',
  fontSize: '10px',
  fontWeight: 600,
  fontFamily: 'Inter, sans-serif',
  backgroundColor: level === 'Low' ? '#dcfce7' : 
                   level === 'Medium' ? '#fef3c7' : '#fee2e2',
  color: level === 'Low' ? '#166534' : 
         level === 'Medium' ? '#92400e' : '#991b1b'
}));

const ExpandButton = styled(Button)(({ theme }) => ({
  color: '#FFFFFF',
  backgroundColor: '#0B91D6',
  fontFamily: 'Inter, sans-serif',
  fontSize: '13px',
  fontWeight: 600,
  textTransform: 'none',
  borderRadius: '4px',
  padding: '10px 16px',
  minHeight: '44px',
  alignSelf: 'center',
  WebkitTapHighlightColor: 'transparent',
  '&:hover': {
    backgroundColor: '#0A7BC4'
  },
  '&:active': {
    transform: 'scale(0.98)'
  },
  [theme.breakpoints.up('sm')]: {
    fontSize: '14px',
    minHeight: '40px',
    padding: '8px 16px'
  },
  [theme.breakpoints.up('md')]: {
    '&:active': {
      transform: 'none'
    }
  }
}));

const KeywordsTable = ({ keywordsData, onExpandKeywords, showTop10 = false, isLoading = false }) => {
  const columns = ['Keyword', 'Search Volume', 'Competition', 'Local Relevance'];

  const hasData = keywordsData && keywordsData.length > 0;

  return (
    <SectionContainer>
      <SectionTitle>
        {showTop10 ? 'Top 10 Local SEO Keywords' : 'Top 5 Local SEO Keywords'}
      </SectionTitle>
      
      {isLoading ? (
        <NoDataContainer>
          <CircularProgress size={24} sx={{ color: '#0B91D6', mb: 1 }} />
          <NoDataText>Analyzing local keywords for your business...</NoDataText>
        </NoDataContainer>
      ) : !hasData ? (
        <NoDataContainer>
          <NoDataText>No keyword data available. Please try again later.</NoDataText>
        </NoDataContainer>
      ) : (
        <>
          <StyledTableContainer component={Paper}>
            <Table>
              <StyledTableHead>
                <TableRow>
                  {columns.map((column, index) => (
                    <HeaderCell key={index}>{column}</HeaderCell>
                  ))}
                </TableRow>
              </StyledTableHead>
              <TableBody>
                {keywordsData.map((item, index) => (
                  <TableRow key={index}>
                    <DataCell>
                      <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#121927' }}>
                        {item.searchTerm}
                      </Typography>
                    </DataCell>
                    <DataCell>
                      <VolumeContainer>
                        <VolumeText>{(item.searchVolume || 0).toLocaleString()}/mo</VolumeText>
                      </VolumeContainer>
                    </DataCell>
                    <DataCell>
                      <CompetitionBadge level={item.competition || 'Medium'}>
                        {item.competition || 'Medium'}
                      </CompetitionBadge>
                    </DataCell>
                    <DataCell>
                      <LocalRelevanceBadge relevance={item.localRelevance || 'Standard'}>
                        {item.localRelevance || 'Standard'}
                      </LocalRelevanceBadge>
                    </DataCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </StyledTableContainer>

          <ExpandButton 
            onClick={onExpandKeywords}
            disabled={isLoading}
          >
            {showTop10 ? 'Show Top 5 Keywords' : 'Get More Keywords'}
          </ExpandButton>
        </>
      )}
    </SectionContainer>
  );
};

export default KeywordsTable;